import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Divider,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Stack,
    Autocomplete,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    Preview as PreviewIcon,
    Receipt as GenerateIcon,
    Close as CloseIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { invoiceService } from '../../../services/invoiceService';
import { toLocalYmd } from '../../../utils/formatters';

const InvoiceForm = ({ onSuccess, onCancel }) => {
    const { handleApiCallWithLoading, showError, showSuccess } = useApiHandler();
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [customerSearchText, setCustomerSearchText] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    // To-pay recipients are not in the customer master; they are picked by GSTIN
    const [partyOptions, setPartyOptions] = useState([]);
    const [partyLoading, setPartyLoading] = useState(false);
    const [partySearchText, setPartySearchText] = useState('');
    const [selectedParty, setSelectedParty] = useState(null);
    const [formData, setFormData] = useState({
        invoice_type: 'credit',
        customer_id: '',
        gst_no: '',
        billing_name: '',
        from_date: '',
        to_date: '',
        notes: '',
        reverse_charge: 'no',
        custom_invoice_no: '',
        invoice_date: ''
    });
    const [errors, setErrors] = useState({});
    const [preview, setPreview] = useState(null);
    
    // Debounced search function
    const debouncedSearch = useCallback((searchFunction, delay = 300) => {
        let timeoutId = null;
        
        return (...args) => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            
            timeoutId = setTimeout(() => {
                searchFunction(...args);
            }, delay);
        };
    }, []);

    useEffect(() => {
        // Set default date range to current month
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        setFormData(prev => ({
            ...prev,
            from_date: toLocalYmd(startOfMonth),
            to_date: toLocalYmd(endOfMonth)
        }));
    }, []);

    // Customer search functions with debouncing
    const searchCustomers = useCallback(async (searchText) => {
        if (searchText.length < 2) return;

        setCustomerLoading(true);
        try {
            const response = await invoiceService.searchCustomers(searchText);
            if (response.success) {
                setCustomerOptions(response.data);
            }
        } catch (error) {
            console.error('Failed to search customers:', error);
        } finally {
            setCustomerLoading(false);
        }
    }, []);

    const debouncedSearchCustomers = useMemo(() => 
        debouncedSearch(searchCustomers), [debouncedSearch, searchCustomers]
    );

    const searchParties = useCallback(async (searchText) => {
        if (searchText.length < 2) return;

        setPartyLoading(true);
        try {
            const response = await invoiceService.searchToPayParties(searchText);
            if (response.success) {
                setPartyOptions(response.data);
            }
        } catch (error) {
            console.error('Failed to search to-pay customers:', error);
        } finally {
            setPartyLoading(false);
        }
    }, []);

    const debouncedSearchParties = useMemo(() =>
        debouncedSearch(searchParties), [debouncedSearch, searchParties]
    );

    const isToPay = formData.invoice_type === 'to_pay';

    const handleInvoiceTypeChange = (event, value) => {
        if (!value || value === formData.invoice_type) return;

        setFormData(prev => ({
            ...prev,
            invoice_type: value,
            customer_id: '',
            gst_no: '',
            billing_name: ''
        }));
        setSelectedCustomer(null);
        setCustomerSearchText('');
        setSelectedParty(null);
        setPartySearchText('');
        setErrors({});
        setPreview(null);
    };

    const handlePartySelect = (value) => {
        setSelectedParty(value);
        setFormData(prev => ({
            ...prev,
            gst_no: value ? value.gst_no : '',
            billing_name: value ? value.name : ''
        }));

        if (errors.gst_no) {
            setErrors(prev => ({ ...prev, gst_no: '' }));
        }
        if (preview) {
            setPreview(null);
        }
    };

    const handleCustomerSelect = (value) => {
        setSelectedCustomer(value);
        if (value) {
            setFormData(prev => ({
                ...prev,
                customer_id: value.id
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                customer_id: ''
            }));
        }
        
        // Clear error when customer is selected
        if (errors.customer_id) {
            setErrors(prev => ({
                ...prev,
                customer_id: ''
            }));
        }

        // Clear preview when customer changes
        if (preview) {
            setPreview(null);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }

        // Clear preview when form data changes
        if (preview) {
            setPreview(null);
        }
    };

    const validateForm = () => {
        const validation = invoiceService.validateInvoiceData(formData);
        setErrors(validation.errors);
        return validation.isValid;
    };

    const handlePreview = async () => {
        if (!validateForm()) {
            showError('Please fix the form errors');
            return;
        }

        setPreviewLoading(true);
        try {
            const response = isToPay
                ? await invoiceService.previewToPayInvoice(formData)
                : await invoiceService.previewInvoice(formData);
            if (response.success) {
                setPreview(response.data);
            } else {
                // Show more informative error message for delivery requirement
                if (response.message && response.message.includes('delivered')) {
                    showError('No delivered bookings found for the selected date range. Only delivered shipments can be invoiced.');
                } else {
                    showError(response.message || 'Failed to generate preview');
                }
            }
        } catch (error) {
            console.error('Error generating preview:', error);
            showError('Error generating preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            showError('Please fix the form errors');
            return;
        }

        setLoading(true);
        try {
            const response = isToPay
                ? await invoiceService.generateToPayInvoice(formData)
                : await invoiceService.generateInvoice(formData);
            if (response.success) {
                showSuccess('Invoice generated successfully');
                onSuccess();
            } else {
                if (response.errors) {
                    setErrors(response.errors);
                }
                // Show more informative error message for delivery requirement
                if (response.message && response.message.includes('delivered')) {
                    showError('No delivered bookings found for the selected date range. Only delivered shipments can be invoiced.');
                } else {
                    showError(response.message || 'Failed to generate invoice');
                }
            }
        } catch (error) {
            if (error.response?.data?.message) {
                showError(error.response.data.message);
            } else {
                console.error('Error generating invoice:', error);
                showError('Error generating invoice');
            }
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return invoiceService.formatCurrency(amount);
    };

    const formatDate = (dateString) => {
        return invoiceService.formatDate(dateString);
    };

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" component="h2">
                    Generate Invoice
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={onCancel}
                >
                    Close
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Form Section */}
                <Grid item xs={12} md={preview ? 6 : 12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Invoice Details
                            </Typography>
                            
                            <ToggleButtonGroup
                                value={formData.invoice_type}
                                exclusive
                                onChange={handleInvoiceTypeChange}
                                size="small"
                                color="primary"
                                sx={{ mb: 2 }}
                            >
                                <ToggleButton value="credit">Credit Customer</ToggleButton>
                                <ToggleButton value="to_pay">To-Pay Customer (GST)</ToggleButton>
                            </ToggleButtonGroup>

                            <Grid container spacing={2}>
                                {isToPay ? (
                                    <>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                options={partyOptions}
                                                filterOptions={(options) => options}
                                                getOptionLabel={(option) => `${option.gst_no} - ${option.name || 'N/A'}`}
                                                isOptionEqualToValue={(option, value) => option.gst_no === value.gst_no}
                                                loading={partyLoading}
                                                value={selectedParty}
                                                inputValue={partySearchText}
                                                onInputChange={(event, newInputValue) => {
                                                    setPartySearchText(newInputValue);
                                                    debouncedSearchParties(newInputValue);
                                                }}
                                                onChange={(event, value) => handlePartySelect(value)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Search GST Number or Customer Name"
                                                        placeholder="Type GSTIN or recipient name..."
                                                        error={!!errors.gst_no}
                                                        helperText={errors.gst_no || 'To-pay recipients with delivered bookings not yet invoiced'}
                                                        required
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                                            endAdornment: partyLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
                                                        }}
                                                    />
                                                )}
                                                renderOption={(props, option) => (
                                                    <Box component="li" {...props} key={option.gst_no}>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {option.name || 'N/A'} ({option.gst_no})
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {[option.city, option.state].filter(Boolean).join(', ')} • {option.unbilled_count} unbilled • {formatCurrency(option.unbilled_amount)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Customer Name on Invoice"
                                                value={formData.billing_name}
                                                onChange={(e) => handleInputChange('billing_name', e.target.value)}
                                                error={!!errors.billing_name}
                                                helperText={errors.billing_name || 'Taken from the latest booking for this GST number. Edit if needed.'}
                                                disabled={!selectedParty}
                                                inputProps={{ maxLength: 255 }}
                                            />
                                        </Grid>
                                    </>
                                ) : (
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            options={customerOptions}
                                            getOptionLabel={(option) => `${option.cust_name} (${option.cust_code}) - ${option.from_company || 'N/A'}`}
                                            loading={customerLoading}
                                            value={selectedCustomer}
                                            inputValue={customerSearchText}
                                            onInputChange={(event, newInputValue) => {
                                                setCustomerSearchText(newInputValue);
                                                debouncedSearchCustomers(newInputValue);
                                            }}
                                            onChange={(event, value) => handleCustomerSelect(value)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Search Customer"
                                                    placeholder="Type to search customers..."
                                                    error={!!errors.customer_id}
                                                    helperText={errors.customer_id || 'Search and select a customer to generate invoice'}
                                                    required
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                                        endAdornment: customerLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props}>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {option.cust_name} ({option.cust_code})
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {option.from_company} • {option.from_email}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="From Date"
                                        type="date"
                                        value={formData.from_date}
                                        onChange={(e) => handleInputChange('from_date', e.target.value)}
                                        error={!!errors.from_date}
                                        helperText={errors.from_date}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="To Date"
                                        type="date"
                                        value={formData.to_date}
                                        onChange={(e) => handleInputChange('to_date', e.target.value)}
                                        error={!!errors.to_date}
                                        helperText={errors.to_date}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Invoice Date (Optional)"
                                        type="date"
                                        value={formData.invoice_date}
                                        onChange={(e) => {
                                            handleInputChange('invoice_date', e.target.value);
                                        }}
                                        error={!!errors.invoice_date}
                                        helperText={errors.invoice_date || 'Leave empty to use current date. Due date will be calculated from this date.'}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Custom Invoice Number (Optional)"
                                        value={formData.custom_invoice_no}
                                        onChange={(e) => handleInputChange('custom_invoice_no', e.target.value)}
                                        error={!!errors.custom_invoice_no}
                                        helperText={errors.custom_invoice_no || 'Leave empty to auto-generate invoice number. Only letters, numbers, hyphens, underscores, and forward slashes are allowed.'}
                                        placeholder="e.g., INV-2024-001, CUSTOM/001, etc."
                                        inputProps={{
                                            maxLength: 50,
                                            pattern: '[A-Za-z0-9\\-_\\/]+'
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Reverse Charge</InputLabel>
                                        <Select
                                            value={formData.reverse_charge}
                                            onChange={(e) => handleInputChange('reverse_charge', e.target.value)}
                                            label="Reverse Charge"
                                            error={!!errors.reverse_charge}
                                        >
                                            <MenuItem value="no">No</MenuItem>
                                            <MenuItem value="yes">Yes</MenuItem>
                                        </Select>
                                        {errors.reverse_charge && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                {errors.reverse_charge}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Notes (Optional)"
                                        multiline
                                        rows={3}
                                        value={formData.notes}
                                        onChange={(e) => handleInputChange('notes', e.target.value)}
                                        placeholder="Add any notes for this invoice..."
                                    />
                                </Grid>
                            </Grid>

                            {/* Information Alert */}
                            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Note:</strong> Only delivered shipments can be invoiced. The system will automatically filter out any bookings that haven't been delivered yet. You can provide a custom invoice number or leave it empty to auto-generate one.
                                </Typography>
                                {isToPay && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        To-pay invoices cover instant to-pay bookings where this GST number is the recipient, as in the instant booking GST report. The due date is 30 days from the invoice date.
                                    </Typography>
                                )}
                            </Alert>

                            <Divider sx={{ my: 3 }} />

                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<PreviewIcon />}
                                    onClick={handlePreview}
                                    disabled={previewLoading || !(isToPay ? formData.gst_no : formData.customer_id) || !formData.from_date || !formData.to_date}
                                >
                                    {previewLoading ? <CircularProgress size={20} /> : 'Preview'}
                                </Button>
                                
                                <Button
                                    variant="contained"
                                    startIcon={<GenerateIcon />}
                                    onClick={handleSubmit}
                                    disabled={loading || !preview}
                                >
                                    {loading ? <CircularProgress size={20} /> : 'Generate Invoice'}
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Preview Section */}
                {preview && (
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Invoice Preview (Delivered Bookings Only)
                                </Typography>

                                {/* Invoice Number Preview */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Invoice Number
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium" color="primary">
                                        {preview.preview_invoice_no}
                                    </Typography>
                                </Box>

                                {/* Customer Info */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Customer
                                    </Typography>
                                    {preview.invoice_type === 'to_pay' ? (
                                        <>
                                            <Typography variant="body1" fontWeight="medium">
                                                {preview.billing_party?.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                GSTIN: {preview.billing_party?.gst_no}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {[preview.billing_party?.address, preview.billing_party?.city, preview.billing_party?.state, preview.billing_party?.pincode].filter(Boolean).join(', ')}
                                            </Typography>
                                            <Chip label="To-Pay" size="small" color="warning" sx={{ mt: 0.5 }} />
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="body1" fontWeight="medium">
                                                {preview.customer?.from_name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {preview.customer?.cust_code}
                                            </Typography>
                                        </>
                                    )}
                                </Box>



                                {/* Billing Information */}
                                {preview.billing_info && Object.keys(preview.billing_info.billing_breakdown).length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Billing Breakdown
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            Billing information is based on the payment mode of each booking
                                        </Typography>
                                        {Object.entries(preview.billing_info.billing_breakdown).map(([paymentMode, customers]) => (
                                            <Box key={paymentMode} sx={{ mb: 1 }}>
                                                <Chip 
                                                    label={paymentMode} 
                                                    size="small" 
                                                    color="primary" 
                                                    sx={{ mb: 1 }}
                                                />
                                                {customers.map((customer, index) => (
                                                    <Box key={index} sx={{ ml: 2, mb: 0.5 }}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {customer.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {customer.count} shipments - {formatCurrency(customer.grand_total)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {/* Date Range */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Billing Period
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatDate(preview.date_range.from)} - {formatDate(preview.date_range.to)}
                                    </Typography>
                                </Box>

                                {/* Invoice Date */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Invoice Date
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {preview.invoice_date ? formatDate(preview.invoice_date) : 'Today'}
                                    </Typography>
                                </Box>

                                {/* Due Date */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Due Date
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {preview.due_date ? formatDate(preview.due_date) : 'N/A'}
                                    </Typography>
                                </Box>

                                {/* Reverse Charge */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Reverse Charge
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {formData.reverse_charge === 'yes' ? 'Yes' : 'No'}
                                    </Typography>
                                </Box>

                                {/* Summary */}
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <Card variant="outlined">
                                            <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                                <Typography variant="h6" color="primary">
                                                    {preview.total_shipments}
                                                </Typography>
                                                <Typography variant="caption">
                                                    Shipments
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Card variant="outlined">
                                            <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                                <Typography variant="h6" color="success.main">
                                                    {formatCurrency(Math.round(preview.grand_total || 0))}
                                                </Typography>
                                                <Typography variant="caption">
                                                    Grand Total
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>

                                {/* Payment Mode Breakdown */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Payment Mode Breakdown
                                    </Typography>
                                    <Stack spacing={1}>
                                        {preview.payment_mode_breakdown?.map((mode, index) => (
                                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        label={mode.payment_mode}
                                                        size="small"
                                                        color="info"
                                                    />
                                                    <Typography variant="body2">
                                                        ({mode.count} shipments)
                                                    </Typography>
                                                </Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {formatCurrency(mode.grand_total)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>

                                {/* Sample Bookings */}
                                {preview.bookings && preview.bookings.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Sample Delivered Bookings (First 5)
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>AWB No.</TableCell>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell align="right">Amount</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {preview.bookings.slice(0, 5).map((booking, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <Typography variant="caption">
                                                                    {booking.awb_no}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption">
                                                                    {formatDate(booking.booking_date)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="caption">
                                                                    {formatCurrency(booking.grand_total)}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {preview.total_shipments > 5 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                ... and {preview.total_shipments - 5} more shipments
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {preview.total_shipments === 0 && (
                                    <Alert severity="warning">
                                        No unbilled bookings found for the selected customer and date range.
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default InvoiceForm; 